import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Plus, Search, Users, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/auth/useAuth';
import { Button } from '@/components/ui/button';
import { DataTable, DataTableToolbar } from '@/components/ui/data-table';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/shell/PageHeader';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

import {
  DEFAULT_USERS_PAGE_SIZE,
  useAdminUserMutations,
  useAdminUsers,
  type AdminUserViewModel,
  type CreateAdminUserInput,
} from '@/features/admin-users/api';
import {
  ADMIN_USERS_HEAD_HEIGHT_PX,
  ADMIN_USERS_ROW_HEIGHT_PX,
  AdminUsersTable,
} from '@/features/admin-users/components/AdminUsersTable';
import { CreateUserDialog } from '@/features/admin-users/components/CreateUserDialog';
import { DeleteUserConfirmDialog } from '@/features/admin-users/components/DeleteUserConfirmDialog';

const EMPTY_CELL = '—';
const ROWS_VISIBLE = 10;
const TABLE_BODY_HEIGHT_PX =
  ADMIN_USERS_HEAD_HEIGHT_PX + ADMIN_USERS_ROW_HEIGHT_PX * ROWS_VISIBLE;

export function AdminUsersPage(): ReactElement {
  const { t } = useTranslation();
  const auth = useAuth();
  const currentUserUuid = auth.user?.id ?? null;

  const [page, setPage] = useState(0);
  const [size, setSize] = useState<number>(DEFAULT_USERS_PAGE_SIZE);
  const [searchInput, setSearchInput] = useState('');
  // Debounce so we don't fire a /users/search round-trip on every keystroke.
  const debouncedQuery = useDebouncedValue(searchInput, 300);

  // Reset to page 0 whenever the active filter changes — otherwise a new search
  // could land on a page index that's out of range for the smaller result set.
  useEffect(() => {
    setPage(0);
  }, [debouncedQuery, size]);

  const usersQuery = useAdminUsers({ page, size, q: debouncedQuery });
  const { create, remove } = useAdminUserMutations();

  const [createOpen, setCreateOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<AdminUserViewModel | null>(null);

  const result = usersQuery.data;
  const users = useMemo(() => (result !== undefined ? [...result.rows].sort(byName) : []), [result]);
  const totalElements = result?.totalElements ?? 0;
  const totalPages = result?.totalPages ?? 1;
  const from = totalElements === 0 ? 0 : page * size + 1;
  const to = Math.min(totalElements, from + users.length - 1);

  function handleCreate(input: CreateAdminUserInput): void {
    create.mutate(input, {
      onSuccess: () => {
        setCreateOpen(false);
        create.reset();
      },
    });
  }

  function handleDeleteConfirm(user: AdminUserViewModel): void {
    if (currentUserUuid !== null && user.uuid === currentUserUuid) {
      setPendingDelete(null);
      return;
    }
    remove.mutate(user.uuid, {
      onSuccess: () => {
        setPendingDelete(null);
        remove.reset();
      },
    });
  }

  function handleDeleteCancel(): void {
    if (remove.isPending) return;
    setPendingDelete(null);
    remove.reset();
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PageHeader
        icon={<Users className="h-[18px] w-[18px]" aria-hidden="true" />}
        title={t('routes.admin.users.heading')}
        subtitle={t('routes.admin.users.subheading')}
        actions={
          <Button
            size="sm"
            onClick={() => {
              create.reset();
              setCreateOpen(true);
            }}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('routes.admin.users.createAction')}
          </Button>
        }
      />
      <div className="flex h-full min-h-0 flex-col gap-4 bg-background px-8 py-5">
        <div className="relative max-w-md">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
            }}
            placeholder={t('routes.admin.users.searchPlaceholder')}
            aria-label={t('routes.admin.users.searchAriaLabel')}
            className="pl-9 pr-9"
            autoComplete="off"
            spellCheck={false}
          />
          {searchInput.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setSearchInput('');
              }}
              aria-label={t('routes.admin.users.clearSearch')}
              className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted"
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          )}
        </div>

        {usersQuery.isPending && <UsersListSkeleton />}

        {usersQuery.isError && (
          <div className="max-w-xl">
            <FetchErrorCallout
              error={usersQuery.error}
              heading={t('routes.admin.users.error.heading')}
              message={t('routes.admin.users.error.message')}
              codeLabel={t('routes.admin.users.error.codeLabel')}
              onRetry={() => {
                usersQuery.refetch().catch(() => undefined);
              }}
              retryLabel={t('routes.admin.users.error.retry')}
            />
          </div>
        )}

        {usersQuery.isSuccess && users.length === 0 && (
          <p className="text-sm text-muted-foreground" role="status">
            {debouncedQuery.trim().length > 0
              ? t('routes.admin.users.searchEmpty', { query: debouncedQuery })
              : t('routes.admin.users.empty')}
          </p>
        )}

        {usersQuery.isSuccess && users.length > 0 && (
          <DataTable
            aria-label={t('routes.admin.users.listAriaLabel')}
            className="flex flex-col"
          >
            <DataTableToolbar
              pageSize={size}
              onPageSizeChange={setSize}
              from={from}
              to={to}
              total={totalElements}
              onPagePrevious={() => {
                setPage((p) => Math.max(0, p - 1));
              }}
              onPageNext={() => {
                setPage((p) => Math.min(totalPages - 1, p + 1));
              }}
              canGoPrevious={page > 0}
              canGoNext={page < totalPages - 1}
              pageSizeSelectId="admin-users-page-size"
              // ~25% shorter toolbar than the default `py-2.5`/`h-8`.
              className="py-1.5"
              buttonClassName="h-7 w-7"
            />
            <div
              className="overflow-auto"
              style={{ height: `${String(TABLE_BODY_HEIGHT_PX)}px` }}
            >
              <AdminUsersTable
                users={users}
                emptyCell={EMPTY_CELL}
                currentUserUuid={currentUserUuid}
                onDelete={(user) => {
                  remove.reset();
                  setPendingDelete(user);
                }}
                pendingDeleteUuid={remove.isPending ? pendingDelete?.uuid ?? null : null}
                userHref={(uuid) => `/admin/users/${encodeURIComponent(uuid)}`}
              />
            </div>
          </DataTable>
        )}
      </div>

      <CreateUserDialog
        open={createOpen}
        onOpenChange={(next) => {
          if (!next && create.isPending) return;
          if (!next) create.reset();
          setCreateOpen(next);
        }}
        onSubmit={handleCreate}
        pending={create.isPending}
        submitError={create.error}
      />

      <DeleteUserConfirmDialog
        user={pendingDelete}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        pending={remove.isPending}
        submitError={remove.error}
      />
    </div>
  );
}

function byName(a: AdminUserViewModel, b: AdminUserViewModel): number {
  const an = a.name.length > 0 ? a.name : a.email;
  const bn = b.name.length > 0 ? b.name : b.email;
  return an.localeCompare(bn);
}

function UsersListSkeleton(): ReactElement {
  return (
    <div className="space-y-2" aria-hidden="true">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full rounded-md" />
      ))}
    </div>
  );
}
